<script lang="ts">
	import Formatted from "@components/common/Formatted.svelte";
	import Icon from "@components/common/Icon.svelte";
	import type { ClassValue, HTMLSelectAttributes } from "svelte/elements";
	import chevronIcon from "@core/assets/icons/chevron.svg?raw";
	import { entries } from "@core/utils/entries.js";
	import { getGlobalTemplateProps } from "../../contexts/getGlobalTemplateProps.js";
	import { customValidationMessages } from "./customValidationMessages.js";

	let {
		value = $bindable(),
		/* eslint-disable prefer-const */
		placeholder,
		children,
		wrapperClass,
		...rest
		/* eslint-enable prefer-const */
	}: Omit<HTMLSelectAttributes, "value"> & {
		wrapperClass?: ClassValue;
		value?: string | number;
	} = $props();

	let validityMessage = $state("");
	const { tenant } = getGlobalTemplateProps();
</script>

<div class={[wrapperClass, "relative"]}>
	<select
		bind:value
		{...rest}
		oninvalid={async (event): Promise<void> => {
			event.preventDefault();
			await rest.oninvalid?.(event);
			const [, message] = entries(customValidationMessages).find(([key]) => event.currentTarget.validity[key]) ?? [];
			validityMessage = message?.(event.currentTarget)[tenant.locale] ?? "";
		}}
		aria-label={placeholder}
		class={[
			"min-h-16 md:min-h-18 border border-fcrlightgray-300 rounded-3xl bg-fcrlightgray-200 enabled:cursor-pointer",
			"text-base text-fcrdarkgray placeholder:text-fcrgray px-6 appearance-none !outline-none disabled:opacity-100",
			validityMessage && "[&:user-invalid]:border-fcrerror [&:user-invalid]:border-2",
			"transition-[padding] duration-200 w-full disabled:text-fcrgray font-medium",
			// If there is a label, change the padding so the label can be there
			placeholder && "[&:not(:placeholder-shown)]:pt-9 [&:not(:placeholder-shown)]:pb-1",
			placeholder && "[&[aria-placeholder]]:pt-9 [&[aria-placeholder]:not(:placeholder-shown)]:pb-1",
			!placeholder && "py-5 items-center",
			value === "" && "!text-transparent",
			rest.class,
		]}
	>
		{#if placeholder}
			<option value="" disabled class="text-fcrgray">{placeholder}</option>
		{/if}
		{@render children?.()}
	</select>
	<Icon
		icon={chevronIcon}
		class="size-6 pointer-events-none [:open+&]:rotate-180 transition-transform absolute right-6 top-1/2 -translate-y-1/2"
	/>
	{#if placeholder}
		<div
			class={[
				"absolute inset-0 text-fcrgray flex px-6 items-center font-medium duration-200",
				"transition-all pointer-events-none size-full [:placeholder-shown+&]:translate-x-px",
				"[:not(:placeholder-shown)+&]:text-xs/6 [:not(:placeholder-shown)+&]:-translate-y-2",
			]}
			aria-hidden="true"
		>
			<span class="h-6 line-clamp-1">
				<Formatted text={placeholder} />
			</span>
		</div>
	{/if}
	<div class="justify-end text-sm text-fcrerror pr-6 pt-1 hidden [select:user-invalid~&:not(:empty)]:flex">
		<Formatted text={validityMessage} />
	</div>
</div>

<style lang="postcss">
	/*
	 * When the browser supports the base-select appearance, make the <select> fits the overall design.
	 * By 2026 we're not expecting most browsers to support this feature, so by @supports we fallback to the native look.
	 */
	@supports (appearance: base-select) {
		select,
		::picker(select) {
			appearance: base-select;
		}

		select::picker-icon {
			display: none;
		}

		::picker(select) {
			border: inherit;
			border-radius: inherit;
			margin: 0.5rem 0;
		}

		select > :global(option) {
			padding: 0.75rem 0.5rem;
			font: inherit;
		}

		select > :global(option:disabled::checkmark) {
			visibility: hidden;
		}

		select > :global(option:enabled:hover) {
			@apply bg-fcrlightgray;
		}

		select > :global(option:enabled) {
			@apply text-fcrdarkgray;
		}

		select > :global(option:disabled) {
			@apply text-fcrgray;
		}
	}
</style>
